import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Smartphone, DollarSign, AlertCircle, List, Activity } from 'react-feather';
import { useAuth } from '../context/AuthContext';
import { depositsAPI } from '../api';
import { formatCurrencyWithConversion, convertToUsd, convertCurrency, getCurrencyInfo, formatCurrency } from '../utils/currency';
import { SWYCHR_MARKETS, getSwychrMarketByCountry } from '../utils/swychrMarkets';

const FALLBACK_XAF_PER_USD = 655.957;

const DEFAULT_SWYCHR_MARKET_CODE = 'CM:XAF';
const ZERO_DECIMAL_CURRENCIES = new Set(['XAF', 'XOF', 'CDF', 'GNF', 'RWF', 'TZS', 'UGX', 'JPY']);
const FAPSHI_PENDING_DEPOSIT_STORAGE_KEY = 'nb_fapshi_pending_deposit_id';
const MONETBIL_PENDING_DEPOSIT_STORAGE_KEY = 'nb_monetbil_pending_deposit_id';
const SWYCHR_PENDING_DEPOSIT_STORAGE_KEY = 'nb_swychr_pending_deposit_id';
const NOTCHPAY_PENDING_DEPOSIT_STORAGE_KEY = 'nb_notchpay_pending_deposit_id';
const SWYCHR_VERIFY_RETRY_COUNT = 6;
const SWYCHR_VERIFY_RETRY_DELAY_MS = 5000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const STATUS_BADGE = {
  pending: { label: 'Pending', color: '#eab308', bg: 'rgba(234,179,8,0.1)' },
  completed: { label: 'Completed', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' }
};

const GATEWAY_INFO = {
  fapshi: {
    depositSteps: [
      'Enter your deposit amount and click Pay to go to checkout.',
      'On the checkout page enter your Mobile Money number and click Deposit.',
      'Approve the push on your MTN MoMo or Orange Money app — your balance is credited within seconds.'
    ]
  },
  notchpay: {
    depositSteps: [
      'Enter your deposit amount and continue to Mobile Money Africa 2 secure checkout.',
      'Select your payment method (MTN Mobile Money, Orange Money, or Card) and complete payment.',
      'After payment is confirmed, your NiceBooster balance is credited immediately.'
    ]
  },
  monetbil: {
    depositSteps: [
      'Enter your deposit amount and continue to Monetbil checkout.',
      'Choose a supported mobile money option and complete the payment.',
      'After Monetbil confirms the payment, your NiceBooster balance is credited automatically.'
    ]
  },
  swychr: {
    depositSteps: [
      'Select your country and enter your deposit amount in the local currency.',
      'Enter your mobile number to receive an instant USSD authorization push directly on your phone.',
      'Approve with your Mobile Money PIN — your NiceBooster balance is credited in real time.'
    ]
  },
  swychr_crypto: {
    depositSteps: [
      'Enter your deposit amount in USD and select your network (TRON TRC-20, BSC BEP-20, or Ethereum ERC-20).',
      'Generate your secure 60-minute Swychr Crypto checkout link.',
      'Send USDT to the address shown. As soon as confirmed on-chain, your balance is credited automatically.'
    ]
  },
  helaket: {
    depositSteps: [
      'Enter your amount in USD and choose the crypto you want to pay with.',
      'Complete the payment on the crypto invoice page.',
      'After the payment is confirmed, your NiceBooster balance is credited automatically.'
    ]
  }
};

const GATEWAYS = [
  {
    id: 'fapshi',
    name: 'Mobile Money',
    sub: 'MTN / Orange · Cameroon',
    icon: <Smartphone size={20} />,
    color: '#f97316',
    desc: 'MTN Mobile Money or Orange Money. Pay directly in FCFA. For Cameroon users.'
  },
  {
    id: 'swychr',
    name: 'Mobile Money Africa',
    sub: 'Africa · 15+ Countries',
    icon: <Smartphone size={20} />,
    color: '#0ea5e9',
    desc: 'Instant direct USSD push in Cameroon and 15+ African countries.'
  },
  {
    id: 'swychr_crypto',
    name: 'Crypto (USDT)',
    sub: 'TRON · BSC · Ethereum',
    icon: <DollarSign size={20} />,
    color: '#10b981',
    desc: 'Pay with USDT via TRON (TRC-20), BSC (BEP-20), or Ethereum.'
  },
  {
    id: 'notchpay',
    name: 'Mobile Money Africa 2',
    sub: 'MTN · Orange · Cards',
    icon: <Smartphone size={20} />,
    color: '#6366f1',
    desc: 'Pay with MTN MoMo, Orange Money, or Credit/Debit Cards.'
  },
  {
    id: 'monetbil',
    name: 'Monetbil',
    sub: 'Mobile Money · FCFA',
    icon: <Smartphone size={20} />,
    color: '#8b5cf6',
    desc: 'Mobile Money in Cameroon, Senegal and across Africa. FCFA or local currency.'
  },
  {
    id: 'helaket',
    name: 'Helaket Crypto',
    sub: 'Cryptocurrency · USD',
    icon: <DollarSign size={20} />,
    color: '#f59e0b',
    desc: 'Pay with USDT, Bitcoin, Ethereum, or Litecoin.',
  }
];

const GATEWAY_VERIFY_CONFIG = {
  fapshi: {
    verify: depositsAPI.verifyFapshi,
    storageKey: FAPSHI_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money payment completed. Your balance will update automatically once confirmed.',
  },
  monetbil: {
    verify: depositsAPI.verifyMonetbil,
    storageKey: MONETBIL_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Monetbil payment confirmed and your balance has been credited.',
    pendingMessage: 'Monetbil payment received. Verification is in progress.',
    fallbackMessage: 'Monetbil payment completed. Your balance will update automatically once confirmed.',
  },
  swychr: {
    verify: depositsAPI.verifySwychr,
    storageKey: SWYCHR_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money Africa payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money Africa payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money Africa payment completed. Verification is still pending.',
  },
  swychr_crypto: {
    verify: depositsAPI.verifySwychr,
    storageKey: 'nb_swychr_crypto_pending_deposit_id',
    completedMessage: 'Crypto payment confirmed and your balance has been credited.',
    pendingMessage: 'Crypto payment received. Verification is in progress.',
    fallbackMessage: 'Crypto payment completed. Verification is still pending.',
  },
  notchpay: {
    verify: depositsAPI.verifyNotchpay,
    storageKey: NOTCHPAY_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money Africa 2 payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money Africa 2 payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money Africa 2 payment completed. Verification is in progress.',
  },
};

export default function Deposit() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [gateway, setGateway] = useState('fapshi');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USDT');
  const [cryptoChain, setCryptoChain] = useState('TRON');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [historyView, setHistoryView] = useState('recent');
  const [rateInfo, setRateInfo] = useState(null);
  const [depositFeatures, setDepositFeatures] = useState(null);
  const [swychrMarketCode, setSwychrMarketCode] = useState(DEFAULT_SWYCHR_MARKET_CODE);
  const [swychrPhone, setSwychrPhone] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const autoCheckedSwychrRef = useRef(false);
  const [fapshiPhone, setFapshiPhone] = useState('');
  const [fapshiPendingId, setFapshiPendingId] = useState(null);
  const [fapshiPollStatus, setFapshiPollStatus] = useState(null); // 'pending' | 'completed' | 'failed'
  const fapshiPollRef = useRef(null);

  useEffect(() => {
    if (user?.phone && !swychrPhone) {
      setSwychrPhone(user.phone);
    }
  }, [user?.phone, swychrPhone]);

  useEffect(() => {
    depositsAPI.history()
      .then((r) => setHistory(r.data.deposits || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []);

  useEffect(() => {
    depositsAPI.rateInfo()
      .then((r) => {
        setRateInfo(r.data.rate || null);
        setDepositFeatures({
          bonuses: r.data.bonuses || null,
          gateways: r.data.gateways || null,
          swychrMarkets: r.data.swychr_markets || [],
        });
      })
      .catch(() => {
        setRateInfo(null);
        setDepositFeatures(null);
      });
  }, []);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const fapshiStatus = searchParams.get('status');
    const gatewayParam = paymentStatus
      ? searchParams.get('gateway')
      : (fapshiStatus === 'SUCCESSFUL' && searchParams.get('transId') ? 'fapshi' : '');
    const depositIdParam = searchParams.get('deposit_id');
    const normalizedPaymentStatus = paymentStatus || (gatewayParam === 'fapshi' && fapshiStatus === 'SUCCESSFUL' ? 'success' : '');

    if (!normalizedPaymentStatus) return;

    if (normalizedPaymentStatus === 'success') {
      setError('');

      const handleSuccessReturn = async () => {
        try {
          const verifyConfig = GATEWAY_VERIFY_CONFIG[gatewayParam];
          if (verifyConfig) {
            const storedDepositId = sessionStorage.getItem(verifyConfig.storageKey);
            const gatewayDepositId = depositIdParam || storedDepositId;

            if (!gatewayDepositId) {
              setSuccess(verifyConfig.fallbackMessage);
              return;
            }

            let lastVerifyMessage = verifyConfig.pendingMessage;
            let verifyCompleted = false;

            for (let attempt = 0; attempt < SWYCHR_VERIFY_RETRY_COUNT; attempt += 1) {
              const verifyRes = await verifyConfig.verify(gatewayDepositId);
              const verifyMessage = verifyRes.data?.message || verifyConfig.pendingMessage;
              const verifyStatus = verifyRes.data?.status || 'pending';

              lastVerifyMessage = verifyMessage;

              if (verifyStatus === 'completed') {
                verifyCompleted = true;
                break;
              }

              if (attempt < SWYCHR_VERIFY_RETRY_COUNT - 1) {
                setSuccess(`Payment is still pending. Rechecking in ${Math.ceil(SWYCHR_VERIFY_RETRY_DELAY_MS / 1000)} seconds...`);
                await delay(SWYCHR_VERIFY_RETRY_DELAY_MS);
              }
            }

            if (verifyCompleted) {
              setSuccess(verifyConfig.completedMessage);
              sessionStorage.removeItem(verifyConfig.storageKey);
            } else {
              setSuccess(lastVerifyMessage);
            }
          } else {
            setSuccess(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} completed. Your balance will update automatically once confirmed.`);
          }
        } catch (err) {
          const verifyError = err.response?.data?.error || err.message;
          setSuccess(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} completed. Verification is still pending.${verifyError ? ` ${verifyError}` : ''}`);
        } finally {
          refreshUser();
          depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
        }
      };

      handleSuccessReturn();
    } else if (normalizedPaymentStatus === 'cancelled') {
      if (gatewayParam && GATEWAY_VERIFY_CONFIG[gatewayParam]) {
        sessionStorage.removeItem(GATEWAY_VERIFY_CONFIG[gatewayParam].storageKey);
      }
      setError(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} was cancelled. You can try again anytime.`);
      setSuccess('');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('payment');
    nextParams.delete('gateway');
    nextParams.delete('deposit_id');
    nextParams.delete('status');
    nextParams.delete('transId');
    nextParams.delete('payment_status');
    nextParams.delete('payment_ref');
    setSearchParams(nextParams, { replace: true });
  }, [refreshUser, searchParams, setSearchParams]);

  useEffect(() => {
    const hasReturnParams =
      searchParams.has('payment') ||
      searchParams.has('gateway') ||
      searchParams.has('deposit_id') ||
      searchParams.has('status') ||
      searchParams.has('transId');

    if (hasReturnParams || autoCheckedSwychrRef.current) return;

    const pendingDepositId = sessionStorage.getItem(SWYCHR_PENDING_DEPOSIT_STORAGE_KEY);
    if (!pendingDepositId) return;

    autoCheckedSwychrRef.current = true;

    const verifyPendingSwychrDeposit = async () => {
      try {
        const res = await depositsAPI.verifySwychr(pendingDepositId);
        if (res.data?.status === 'completed') {
          setSuccess('Swychr payment confirmed and your balance has been credited.');
          setError('');
          sessionStorage.removeItem(SWYCHR_PENDING_DEPOSIT_STORAGE_KEY);
        }
      } catch (err) {
        // Keep this silent on normal page load; the manual Verify button remains available.
      } finally {
        refreshUser();
        depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
      }
    };

    verifyPendingSwychrDeposit();
  }, [refreshUser, searchParams]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fapshi Direct Pay polling — runs in DepositCheckout instead
  // (kept as no-op to avoid breaking state refs used during checkout navigation)

  const switchGateway = (gw) => {
    setGateway(gw);
    setAmount('');
    setError('');
    setSuccess('');
    setFapshiPendingId(null);
    setFapshiPollStatus(null);
  };

  const isFapshi = gateway === 'fapshi';
  const isMonetbil = gateway === 'monetbil';
  const isSwychr = gateway === 'swychr';
  const isNotchpay = gateway === 'notchpay';
  const isSwychrCrypto = gateway === 'swychr_crypto';
  const isHelaket = gateway === 'helaket';
  const isMultiCountry = isSwychr || isNotchpay;
  const isMobileMoneyGateway = isFapshi || isMonetbil || isSwychr || isNotchpay;
  const isCryptoGateway = isHelaket || isSwychrCrypto;
  const gatewayEnabledMap = depositFeatures?.gateways || { fapshi: true, notchpay: true, monetbil: false, helaket: false, swychr: false, swychr_crypto: true };
  const availableGateways = GATEWAYS.filter((gw) => gatewayEnabledMap[gw.id] !== false);
  const swychrMarkets = SWYCHR_MARKETS;
  const amountNum = parseFloat(amount) || 0;
  const userCurrency = user?.currency || 'USD';
  const balanceDisplay = formatCurrencyWithConversion(user?.balance || 0, user?.currency || 'USD');
  const gatewayInfo = GATEWAY_INFO[gateway] || GATEWAY_INFO.fapshi;
  const effectiveXafPerUsd = rateInfo?.effectiveRate || FALLBACK_XAF_PER_USD;
  const selectedSwychrMarket = swychrMarkets.find(
    (market) => `${market.countryCode}:${market.currency}` === swychrMarketCode
  ) || swychrMarkets[0] || null;
  const inputCurrency = isMultiCountry ? (selectedSwychrMarket?.currency || 'XAF') : userCurrency;
  const inputCurrencyInfo = getCurrencyInfo(inputCurrency);
  const inputDecimals = ZERO_DECIMAL_CURRENCIES.has(inputCurrency) ? 0 : 2;
  const accountAmountUsd = isSwychr
    ? convertToUsd(amountNum, inputCurrency)
    : convertToUsd(amountNum, userCurrency);
  const mobileGatewayAmountXaf = Math.round(accountAmountUsd * effectiveXafPerUsd);
  const swychrLocalDecimals = ZERO_DECIMAL_CURRENCIES.has(selectedSwychrMarket?.currency || '') ? 0 : 2;
  const swychrLocalAmount = amountNum;
  const minDepositConfig = depositFeatures?.min_deposit;
  const isMinDepositEnforced = Boolean(minDepositConfig && minDepositConfig.enabled === true);
  const configuredMinUsd = Number(minDepositConfig?.amount_usd) || 0;
  const configuredMinXaf = minDepositConfig?.amount_xaf ? Number(minDepositConfig.amount_xaf) : null;
  const cryptoMinUsd = Number(minDepositConfig?.crypto_min_deposit_usd) || 0;
  const isCryptoMinEnforced = isSwychrCrypto && cryptoMinUsd > 0;
  const minimumDepositUsd = isCryptoMinEnforced
    ? cryptoMinUsd
    : isMinDepositEnforced
    ? (configuredMinXaf ? configuredMinXaf / effectiveXafPerUsd : configuredMinUsd)
    : 0.1;
  const minimumDepositInInputCurrency = isCryptoMinEnforced
    ? convertCurrency(cryptoMinUsd, inputCurrency)
    : (isMinDepositEnforced && inputCurrency === 'XAF' && configuredMinXaf)
    ? configuredMinXaf
    : convertCurrency(minimumDepositUsd, inputCurrency);
  const quickChipDecimals = inputDecimals;
  const estimatedCreditLabel = amountNum > 0
    ? `Estimated credit: ${formatCurrencyWithConversion(accountAmountUsd, userCurrency, ZERO_DECIMAL_CURRENCIES.has(userCurrency) ? 0 : 2)}`
    : '';
  const swychrChargeLabel = isMultiCountry && selectedSwychrMarket && amountNum > 0
    ? `Paying in ${selectedSwychrMarket.label}: ${formatCurrency(swychrLocalAmount, selectedSwychrMarket.currency, swychrLocalDecimals)}`
    : '';
  const minimumDepositLabel = isCryptoMinEnforced
    ? `$${cryptoMinUsd.toFixed(2)} USD minimum`
    : isMinDepositEnforced
    ? (isMultiCountry
        ? `${formatCurrency(minimumDepositInInputCurrency, inputCurrency, quickChipDecimals)} minimum`
        : `${formatCurrencyWithConversion(minimumDepositUsd, userCurrency, quickChipDecimals)} minimum`)
    : '';
  const firstDepositBonus = depositFeatures?.bonuses;

  useEffect(() => {
    if (!availableGateways.length) return;
    if (!availableGateways.some((gw) => gw.id === gateway)) {
      setGateway(availableGateways[0].id);
    }
  }, [availableGateways, gateway]);

  useEffect(() => {
    if (!swychrMarkets.length) return;
    if (!swychrMarkets.some((market) => `${market.countryCode}:${market.currency}` === swychrMarketCode)) {
      setSwychrMarketCode(`${swychrMarkets[0].countryCode}:${swychrMarkets[0].currency}`);
    }
  }, [swychrMarketCode, swychrMarkets]);

  useEffect(() => {
    if (!swychrMarkets.length) return;
    const preferredMarket = getSwychrMarketByCountry(user?.country_code);
    if (!preferredMarket) return;
    const preferredMarketCode = `${preferredMarket.countryCode}:${preferredMarket.currency}`;
    if (swychrMarkets.some((market) => `${market.countryCode}:${market.currency}` === preferredMarketCode)) {
      setSwychrMarketCode(preferredMarketCode);
    }
  }, [swychrMarkets, user?.country_code]);

  const previewLine = amountNum >= minimumDepositInInputCurrency
    ? [estimatedCreditLabel, swychrChargeLabel].filter(Boolean).join(' | ')
    : '';
  const visibleHistory = historyView === 'all' ? history : history.slice(0, 5);

  const renderHistoryCard = () => (
    <div className="card" style={{ marginBottom: isMobile ? 20 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, margin: 0 }}><List size={16} style={{ marginRight: 6 }} /> Recent Deposits</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {history.length > 5 && (
            <select
              className="form-input"
              value={historyView}
              onChange={(e) => setHistoryView(e.target.value)}
              style={{ width: 110, padding: '6px 10px', fontSize: 12, minHeight: 'auto' }}
            >
              <option value="recent">Last 5</option>
              <option value="all">View all</option>
            </select>
          )}
          {isMobile && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Mobile View</span>}
        </div>
      </div>

      {histLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)' }}>
          <AlertCircle size={36} style={{ color: 'var(--text-muted)' }} />
          <p style={{ fontSize: 14 }}>No deposits yet</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {visibleHistory.map((deposit) => {
            const meta = STATUS_BADGE[deposit.status] || STATUS_BADGE.pending;
            let localAmount = null;
            let localCurrency = '';
            try {
              const parsedMetadata = JSON.parse(deposit.metadata || '{}');
              localAmount = parsedMetadata.local_amount ?? deposit.xaf_amount ?? null;
              localCurrency = parsedMetadata.currency || (deposit.xaf_amount ? 'XAF' : '');
            } catch {
              localAmount = deposit.xaf_amount;
              localCurrency = deposit.xaf_amount ? 'XAF' : '';
            }

            return (
              <div
                key={deposit.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {formatCurrencyWithConversion(deposit.amount, user?.currency || 'USD')}
                    {localAmount && localCurrency && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6, fontWeight: 400 }}>
                        ({Number(localAmount).toLocaleString()} {localCurrency})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {deposit.gateway === 'helaket' ? 'Crypto' : deposit.gateway === 'fapshi' ? 'Mobile Money' : deposit.gateway === 'notchpay' ? 'Mobile Money Africa 2' : deposit.gateway === 'swychr' ? 'Mobile Money Africa' : deposit.gateway} · {new Date(deposit.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      padding: '3px 10px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      background: meta.bg,
                      color: meta.color,
                      border: `1px solid ${meta.color}44`
                    }}
                  >
                    {meta.label}
                  </span>
                  {(deposit.gateway === 'swychr' || deposit.gateway === 'notchpay') && deposit.status === 'pending' && (
                    <button
                      onClick={() => handleVerifyDeposit(deposit.id, deposit.gateway)}
                      disabled={verifyingId === deposit.id}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--accent)',
                        background: 'transparent',
                        color: 'var(--accent)',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        transition: 'all 0.15s'
                      }}
                    >
                      {verifyingId === deposit.id ? (
                        <>
                          <span className="spinner" style={{ width: 10, height: 10, borderWidth: 1, borderTopColor: 'var(--accent)', marginRight: 2 }} />
                          Checking...
                        </>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          marginTop: 20,
          padding: '12px 14px',
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          fontSize: 12,
          color: 'var(--text-muted)',
          lineHeight: 1.6
        }}
      >
        <strong style={{ color: 'var(--text)', display: 'block', marginBottom: 4 }}>
          <Activity size={14} style={{ marginRight: 4 }} /> How deposits work
        </strong>
        <div>{gatewayInfo.depositSteps[0]}</div>
        <div>{gatewayInfo.depositSteps[1]}</div>
        <div>{gatewayInfo.depositSteps[2]}</div>
        <div>Your balance is shown in your account currency after the deposit is credited.</div>
      </div>
    </div>
  );

  const handleVerifyDeposit = async (depositId) => {
    setVerifyingId(depositId);
    setError('');
    setSuccess('');
    try {
      const res = await depositsAPI.verifySwychr(depositId);
      if (res.data?.status === 'completed') {
        setSuccess('Swychr deposit verified and credited successfully!');
        refreshUser();
        depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
      } else {
        setError(res.data?.message || 'Payment is still pending on Swychr.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Swychr verification failed.');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!availableGateways.length) {
      setError('No payment gateway is currently enabled.');
      return;
    }

    if (isCryptoMinEnforced && accountAmountUsd < cryptoMinUsd) {
      setError(`Minimum crypto deposit is $${cryptoMinUsd.toFixed(2)} USD`);
      return;
    }

    if (isMinDepositEnforced && (!amountNum || amountNum < minimumDepositInInputCurrency)) {
      setError(isMultiCountry ? minimumDepositLabel : `Minimum deposit is ${formatCurrencyWithConversion(minimumDepositUsd, userCurrency, quickChipDecimals)}`);
      return;
    }
    if (!amountNum || amountNum <= 0) {
      setError('Please enter a valid deposit amount.');
      return;
    }

    setLoading(true);

    // Direct Pay gateways (Fapshi & Swychr Direct) — navigate to checkout page which handles the phone input & USSD push
    if (isFapshi || isSwychr) {
      try {
        navigate('/dashboard/deposit/checkout', {
          state: {
            gateway: isSwychr ? 'Mobile Money Africa' : 'Mobile Money',
            gatewayId: gateway,
            isDirectPay: true,
            selectedMarket: isSwychr ? selectedSwychrMarket : null,
            countryCode: isSwychr ? (selectedSwychrMarket?.countryCode || 'CM') : 'CM',
            currency: isSwychr ? (selectedSwychrMarket?.currency || 'XAF') : 'XAF',
            localAmount: isSwychr ? swychrLocalAmount : Math.max(100, mobileGatewayAmountXaf),
            xafAmount: isSwychr ? swychrLocalAmount : Math.max(100, mobileGatewayAmountXaf),
            creditedUsd: accountAmountUsd,
            displayAmount: isSwychr
              ? formatCurrency(swychrLocalAmount, selectedSwychrMarket?.currency || 'XAF', swychrLocalDecimals)
              : formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals),
          },
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      let res;

      if (isNotchpay) {
        if (!selectedSwychrMarket) {
          setError('No market is available right now.');
          return;
        }
        res = await depositsAPI.initiateNotchpay({
          amount_local: swychrLocalAmount,
          country_code: selectedSwychrMarket.countryCode,
          currency: selectedSwychrMarket.currency,
        });
      } else if (isMonetbil) {
        res = await depositsAPI.initiateMonetbil(Math.max(100, mobileGatewayAmountXaf));
      } else if (isSwychrCrypto) {
        res = await depositsAPI.initiateSwychrCrypto({
          amount: accountAmountUsd,
          chain: cryptoChain || 'TRON',
        });
      } else {
        res = await depositsAPI.initiateHelaket(accountAmountUsd, currency);
      }

      const payUrl = res.data.payment_url;
      if (!payUrl) {
        setError('Payment gateway returned no redirect URL. Contact support.');
        return;
      }

      const creditedUsd = isMobileMoneyGateway ? res.data.usd_amount : accountAmountUsd;
      const gwName = isNotchpay ? 'Mobile Money Africa 2' : isMonetbil ? 'Monetbil' : isSwychrCrypto ? 'Crypto' : 'Helaket Crypto';
      if (res.data.deposit_id) {
        if (isMonetbil) {
          sessionStorage.setItem(MONETBIL_PENDING_DEPOSIT_STORAGE_KEY, String(res.data.deposit_id));
        } else if (isSwychrCrypto) {
          sessionStorage.setItem('nb_swychr_crypto_pending_deposit_id', String(res.data.deposit_id));
        }
      }
      navigate('/dashboard/deposit/checkout', {
        state: {
          gateway: gwName,
          gatewayId: gateway,
          paymentUrl: payUrl,
          creditedUsd: parseFloat(creditedUsd || 0),
          displayAmount: isSwychrCrypto
            ? `$${parseFloat(accountAmountUsd).toFixed(2)} USD (${res.data.amount_crypto || accountAmountUsd} USDT - ${res.data.chain || cryptoChain})`
            : formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals),
        },
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || 'Payment initiation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Add Funds</h1>
      <p className="page-subtitle">Choose a payment method to top up your account</p>

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 40,
          padding: '8px 20px',
          marginBottom: 28
        }}
      >
        <span style={{ fontSize: 18 }}></span>
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Current Balance:</span>
        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--green-500)' }}>
          {balanceDisplay}
        </span>
      </div>

      {firstDepositBonus?.first_deposit_enabled &&
        firstDepositBonus?.first_deposit_eligible &&
        Number(firstDepositBonus.first_deposit_percent || 0) > 0 && (
        <div
          className="card"
          style={{
            marginBottom: 20,
            borderColor: 'rgba(59,130,246,0.22)',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.04))'
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6, color: 'var(--accent)' }}>
            First deposit bonus enabled
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Get an extra {firstDepositBonus.first_deposit_percent}% on your first completed deposit.
          </div>
        </div>
      )}

      <div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 20 }}>
            {availableGateways.map((gw) => (
              <div
                key={gw.id}
                onClick={() => switchGateway(gw.id)}
                style={{
                  flex: 1,
                  background: gateway === gw.id ? 'var(--bg-elevated)' : 'var(--bg-card)',
                  border: `2px solid ${gateway === gw.id ? gw.color : 'var(--border)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: '18px 20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: gateway === gw.id ? `0 0 24px ${gw.color}22` : 'none',
                  opacity: 1
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 11,
                      background: `${gw.color}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      flexShrink: 0
                    }}
                  >
                    {gw.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 15 }}>{gw.name}</div>
                    <div style={{ fontSize: 11, color: gw.color, fontWeight: 600 }}>{gw.sub}</div>
                  </div>
                  {gateway === gw.id && (
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: gw.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: '#fff',
                        fontWeight: 700
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{gw.desc}</div>
              </div>
            ))}
          </div>

          {!availableGateways.length && (
            <div className="alert alert-error" style={{ marginBottom: 20 }}>
              All payment gateways are currently disabled by the admin.
            </div>
          )}

          {renderHistoryCard()}

          <div className="card">
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {(isFapshi && fapshiPendingId) ? (
              <div style={{ textAlign: 'center', padding: '36px 0' }}>
                <div style={{
                  width: 52, height: 52, margin: '0 auto 20px',
                  border: '3px solid rgba(249,115,22,0.2)', borderTopColor: '#f97316',
                  borderRadius: '50%', animation: 'spin 0.9s linear infinite'
                }} />
                <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8 }}>Waiting for your approval</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 6, lineHeight: 1.7 }}>
                  A payment push was sent to{' '}
                  <strong style={{ color: 'var(--text)' }}>{fapshiPhone}</strong>.<br />
                  Open <strong>MTN MoMo</strong> or <strong>Orange Money</strong> and approve the request.
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 24 }}>
                  This page updates automatically once payment is confirmed.
                </div>
                <button
                  type="button"
                  onClick={() => { setFapshiPendingId(null); setFapshiPollStatus(null); }}
                  style={{
                    padding: '8px 24px', borderRadius: 8, border: '1px solid var(--border)',
                    background: 'transparent', color: 'var(--text-muted)',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
            <form onSubmit={handleDeposit}>
              {isMultiCountry && (
                <div className="form-group">
                  <label className="form-label">Select your country</label>
                  <select
                    className="form-input"
                    value={swychrMarketCode}
                    onChange={(e) => {
                      setSwychrMarketCode(e.target.value);
                      setAmount('');
                    }}
                  >
                    {swychrMarkets.map((market) => (
                      <option
                        key={`${market.countryCode}:${market.currency}`}
                        value={`${market.countryCode}:${market.currency}`}
                      >
                        {market.flag || ''} {market.label} ({market.currency})
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                    {isNotchpay ? 'Mobile Money Africa 2 will open checkout for the selected country and currency.' : 'Mobile Money Africa will open checkout for the selected country and currency.'}
                  </div>
                </div>
              )}

              {isSwychrCrypto && (
                <div className="form-group">
                  <label className="form-label">Select Network (USDT)</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { id: 'TRON', label: 'TRON (TRC-20)' },
                      { id: 'BSC', label: 'BNB Chain (BEP-20)' },
                      { id: 'ETH', label: 'Ethereum (ERC-20)' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setCryptoChain(item.id)}
                        style={{
                          padding: '8px 18px',
                          borderRadius: 10,
                          border: `1.5px solid ${cryptoChain === item.id ? '#10b981' : 'var(--border)'}`,
                          background: cryptoChain === item.id ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-elevated)',
                          color: cryptoChain === item.id ? '#10b981' : 'var(--text-muted)',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 13,
                          transition: 'all 0.15s',
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
                    Pay in USDT on your chosen network. A secure 60-minute payment invoice will be generated.
                  </div>
                </div>
              )}

              {isHelaket && (
                <div className="form-group">
                  <label className="form-label">Select Crypto</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['USDT', 'BTC', 'ETH', 'LTC'].map((coin) => (
                      <button
                        key={coin}
                        type="button"
                        onClick={() => setCurrency(coin)}
                        style={{
                          padding: '8px 18px',
                          borderRadius: 10,
                          border: `1.5px solid ${currency === coin ? 'var(--accent)' : 'var(--border)'}`,
                          background: currency === coin ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                          color: currency === coin ? 'var(--accent)' : 'var(--text-muted)',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 14,
                          transition: 'all 0.15s',
                          boxShadow: currency === coin ? '0 0 12px var(--accent-glow)' : 'none'
                        }}
                      >
                        {coin}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
                    You can change this again on the checkout page.
                  </div>
                </div>
              )}

              {isFapshi && (
                <div className="form-group" style={{ display: 'none' }}>
                  {/* phone input moved to DepositCheckout */}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Amount in {inputCurrency}
                </label>
                <div style={{ position: 'relative' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      fontSize: 14,
                      fontWeight: 700,
                      pointerEvents: 'none'
                    }}
                  >
                    {inputCurrencyInfo?.symbol || inputCurrency}
                  </span>
                  <input
                    className="form-input"
                    type="number"
                    step={inputDecimals === 0 ? '1' : '0.01'}
                    min={String(minimumDepositInInputCurrency.toFixed(inputDecimals === 0 ? 0 : 2))}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={isMultiCountry ? `e.g. ${inputDecimals === 0 ? '1000' : '1000.00'}` : 'e.g. 10'}
                    style={{ paddingLeft: 58 }}
                    required
                  />
                </div>
                {previewLine && (
                  <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 5, fontWeight: 600 }}>
                    {previewLine}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                  <span>{minimumDepositLabel}</span>
                  {isSwychrCrypto && cryptoMinUsd > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer', border: '1px solid #10b981', color: '#10b981' }}
                      onClick={() => {
                        setAmount(String(minimumDepositInInputCurrency));
                      }}
                    >
                      Convert to my currency ({formatCurrency(minimumDepositInInputCurrency, inputCurrency, quickChipDecimals)})
                    </button>
                  )}
                </div>
              </div>



              <button
                type="submit"
                disabled={loading || !amount}
                className="btn btn-lg"
                style={{
                  width: '100%',
                  marginTop: 8,
                  background: isFapshi ? '#f97316' : isSwychr ? '#0ea5e9' : isSwychrCrypto ? '#10b981' : isNotchpay ? '#6366f1' : isMonetbil ? '#8b5cf6' : '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  boxShadow: loading ? 'none' : `0 4px 20px ${isFapshi ? '#f9731644' : isSwychr ? '#0ea5e944' : isSwychrCrypto ? '#10b98144' : isNotchpay ? '#6366f144' : '#f59e0b44'}`
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderTopColor: '#fff' }} />
                    Processing...
                  </span>
                ) : isFapshi ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Smartphone size={16} />
                    Pay {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} via Mobile Money
                  </span>
                ) : isSwychr ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Smartphone size={16} />
                    Pay {amountNum ? formatCurrency(amountNum, inputCurrency, inputDecimals) : ''} via Mobile Money Africa
                  </span>
                ) : isSwychrCrypto ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <DollarSign size={16} />
                    Pay {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} via Crypto (USDT)
                  </span>
                ) : isNotchpay ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Smartphone size={16} />
                    Pay {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} via Mobile Money Africa 2
                  </span>
                ) : isMonetbil ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Smartphone size={16} />
                    Pay {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} via Monetbil
                  </span>
                ) : (
                  `Pay ${amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} via Crypto`
                )}
              </button>
            </form>
            )}
          </div>
      </div>
    </div>
  );
}
